import { useEffect, useRef } from "react";

const PASTE_OFFSET = 50;

function isTextInput(element) {
  if (!element) return false;
  const tag = element.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA") return true;
  if (element.isContentEditable) return true;
  return false;
}

export default function useCopyPaste({
  nodes,
  selection,
  addDevice,
  addBoundary,
  addCanvasLabel,
  selectDevice,
  selectBoundary,
  selectCanvasLabel,
  readOnly,
}) {
  const clipboard = useRef(null);
  const pasteCount = useRef(0);

  useEffect(() => {
    if (readOnly) return;

    const handler = (e) => {
      if (isTextInput(document.activeElement)) return;
      if (!e.metaKey && !e.ctrlKey) return;
      if (e.shiftKey) return;

      if (e.key === "c") {
        if (!selection) return;
        if (selection.type !== "device" && selection.type !== "boundary" && selection.type !== "canvasLabel") return;

        const node = nodes.find((n) => n.id === selection.id);
        if (!node) return;

        clipboard.current = {
          type: node.type,
          position: { ...node.position },
          style: node.style ? { ...node.style } : undefined,
          zIndex: node.zIndex,
          data: JSON.parse(JSON.stringify(node.data)),
        };
        pasteCount.current = 0;
      }

      if (e.key === "v") {
        if (!clipboard.current) return;
        e.preventDefault();

        pasteCount.current += 1;
        const offset = PASTE_OFFSET * pasteCount.current;
        const newPosition = {
          x: clipboard.current.position.x + offset,
          y: clipboard.current.position.y + offset,
        };

        const newId = crypto.randomUUID();

        if (clipboard.current.type === "device") {
          const clonedData = JSON.parse(JSON.stringify(clipboard.current.data));
          delete clonedData.label;
          const newNode = {
            id: newId,
            type: "device",
            position: newPosition,
            data: clonedData,
          };
          addDevice(newNode);
          selectDevice(newId);
        } else if (clipboard.current.type === "signalBoundary") {
          const newNode = {
            id: newId,
            type: "signalBoundary",
            position: newPosition,
            data: JSON.parse(JSON.stringify(clipboard.current.data)),
          };
          addBoundary(newNode);
          selectBoundary(newId);
        } else if (clipboard.current.type === "canvasLabel") {
          const clonedData = JSON.parse(JSON.stringify(clipboard.current.data));
          const newNode = {
            id: newId,
            type: "canvasLabel",
            position: newPosition,
            style: clipboard.current.style ? { ...clipboard.current.style } : undefined,
            zIndex: clipboard.current.zIndex,
            data: clonedData,
          };
          addCanvasLabel(newNode);
          selectCanvasLabel(newId);
        }
      }
    };

    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [nodes, selection, addDevice, addBoundary, addCanvasLabel, selectDevice, selectBoundary, selectCanvasLabel, readOnly]);
}
